import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { documentsAPI } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useClientConfig } from '../api/clientConfig';
import PolicySearchPanel from '../components/PolicySearchPanel';
import SearchAnswer from '../components/SearchAnswer';
import StartPageWidgets from '../components/StartPageWidgets';
import { scopeFromParams } from '../components/ScopeToggle';
import { extractErrorMessage } from '../utils/errors';

/**
 * The Start Page -- what "/" renders, for everybody (GitHub issue #173).
 *
 * It replaced the Dashboard as the landing screen, and the two answer
 * different people's questions: the Dashboard answers a policy MANAGER's
 * ("how many drafts, what expires when") and is now at /dashboard behind its
 * own grant, while this answers a reader's ("what does our policy actually
 * say"). Every user lands here and nobody needs a permission for it, which is
 * also why components/HomeRoute.js could be deleted -- there is no longer a
 * viewer for whom "/" might render something they cannot see.
 *
 * ACTIVE DOCUMENTS ONLY, throughout, per the issue. There is no status control
 * on this page at all.
 *
 * IT SAYS "DOCUMENT", NOT "POLICY", in its own headings and empty states --
 * issue #172's convention, applied to a new screen for the reason that issue
 * records: this searches the WHOLE corpus, and "Policy" is also the name of one
 * document TYPE within it, so a heading saying "policies" reads as a filter it
 * is not. "Policy Areas" stays, there and everywhere: that is the name of a
 * real lookup table, not a synonym for document.
 *
 * URL-BACKED, with every default expressed by the parameter's ABSENCE (issue
 * #36's precedent): ?q=, ?mode=, ?scope=, ?policy_area_id=,
 * ?regulatory_reference_id=, ?document_type_id=. So a result set is a
 * shareable link, back and
 * forward work, and a reload re-runs exactly what the sharer saw.
 */

function idsFromParams(searchParams, key) {
  // Number(), because LookupMultiSelect matches on selectedIds.includes(item.id)
  // and item.id is what the API returned -- a number. Handing it the strings
  // getAll() produces makes every checkbox read as unticked while the filter is
  // genuinely applied. DocumentsPage.js records the same trap.
  return searchParams.getAll(key).map(Number).filter(Number.isFinite);
}

function StartPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { homeScope } = useAuth();
  const { semantic_search_enabled: semanticAvailable } = useClientConfig();

  const hasHomeScope = Boolean(homeScope?.nodes?.length);

  const [question, setQuestion] = useState(searchParams.get('q') || '');
  const [policyAreaIds, setPolicyAreaIds] = useState(
    () => idsFromParams(searchParams, 'policy_area_id'));
  const [regulatoryReferenceIds, setRegulatoryReferenceIds] = useState(
    () => idsFromParams(searchParams, 'regulatory_reference_id'));
  const [documentTypeIds, setDocumentTypeIds] = useState(
    () => idsFromParams(searchParams, 'document_type_id'));

  const [result, setResult] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  // Read straight from the URL, which stays the source of truth. `mode` falls
  // back to keywords whenever semantic is unavailable, so a shared "ask a
  // question" link still works for a colleague in an environment where it is
  // switched off -- it just answers with word matches.
  const urlMode = searchParams.get('mode');
  const mode = (urlMode === 'semantic' && semanticAvailable) ? 'semantic' : 'lexical';
  const scope = scopeFromParams(searchParams, hasHomeScope);
  const activeQuery = searchParams.get('q') || '';

  const runSearch = useCallback(async () => {
    if (!activeQuery.trim()) {
      setResult(null);
      setDocuments([]);
      return;
    }
    try {
      setSearching(true);
      setError(null);
      if (mode === 'semantic') {
        // One request answers both halves: the prose answer when there is one,
        // and the word-matched list either way. The backend NEVER rejects this
        // for a quota refusal -- it downgrades to lexical and says so in
        // fallback_reason, which SearchAnswer renders as a notice.
        const response = await documentsAPI.searchAnswer({
          question: activeQuery,
          scope,
          policy_area_id: idsFromParams(searchParams, 'policy_area_id'),
          regulatory_reference_id: idsFromParams(searchParams, 'regulatory_reference_id'),
          document_type_id: idsFromParams(searchParams, 'document_type_id'),
        });
        setResult(response.data);
        setDocuments(response.data.documents || []);
      } else {
        // The SAME endpoint and the same filters the Documents page uses, so
        // "Search" means one thing in this application.
        const response = await documentsAPI.list({
          search: activeQuery,
          status: ['active'],
          scope,
          policy_area_id: idsFromParams(searchParams, 'policy_area_id'),
          regulatory_reference_id: idsFromParams(searchParams, 'regulatory_reference_id'),
          document_type_id: idsFromParams(searchParams, 'document_type_id'),
        });
        setResult(null);
        setDocuments(response.data);
      }
    } catch (err) {
      console.error('Error searching policies:', err);
      setError(extractErrorMessage(err));
      setResult(null);
      setDocuments([]);
    } finally {
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, mode, scope, activeQuery]);

  useEffect(() => { runSearch(); }, [runSearch]);

  // Keep the inputs in sync when the URL changes underneath them (a shared
  // link, back/forward).
  useEffect(() => {
    setQuestion(searchParams.get('q') || '');
    setPolicyAreaIds(idsFromParams(searchParams, 'policy_area_id'));
    setRegulatoryReferenceIds(idsFromParams(searchParams, 'regulatory_reference_id'));
    setDocumentTypeIds(idsFromParams(searchParams, 'document_type_id'));
  }, [searchParams]);

  // Shared by the Search button, the mode pills and the scope toggle, so the
  // two-state controls can apply IMMEDIATELY while the text box waits for
  // Search -- without two code paths writing the URL. `overrides` wins over
  // state, since a toggle's onChange fires before its setter has flushed.
  // DocumentsPage.applyFilters' rule.
  const applyFilters = (overrides = {}) => {
    const next = new URLSearchParams(searchParams);
    const nextQuestion = overrides.question !== undefined ? overrides.question : question;
    if (nextQuestion.trim()) next.set('q', nextQuestion);
    else next.delete('q');

    const nextMode = overrides.mode !== undefined ? overrides.mode : mode;
    // Absence means keywords, the page's own default.
    if (nextMode === 'semantic') next.set('mode', 'semantic');
    else next.delete('mode');

    const nextScope = overrides.scope !== undefined ? overrides.scope : scope;
    const defaultScope = hasHomeScope ? 'mine' : 'all';
    if (nextScope === defaultScope) next.delete('scope');
    else next.set('scope', nextScope);

    next.delete('policy_area_id');
    (overrides.policyAreaIds || policyAreaIds).forEach(
      (id) => next.append('policy_area_id', id));
    next.delete('regulatory_reference_id');
    (overrides.regulatoryReferenceIds || regulatoryReferenceIds).forEach(
      (id) => next.append('regulatory_reference_id', id));
    next.delete('document_type_id');
    (overrides.documentTypeIds || documentTypeIds).forEach(
      (id) => next.append('document_type_id', id));

    setSearchParams(next);
  };

  return (
    <div>
      <div className="page-header">
        <h2>🔍 Search Documents</h2>
      </div>

      <PolicySearchPanel
        question={question}
        onQuestionChange={setQuestion}
        mode={mode}
        onModeChange={(m) => applyFilters({ mode: m })}
        semanticAvailable={Boolean(semanticAvailable)}
        scope={scope}
        onScopeChange={(s) => applyFilters({ scope: s })}
        hasHomeScope={hasHomeScope}
        policyAreaIds={policyAreaIds}
        onPolicyAreaChange={setPolicyAreaIds}
        regulatoryReferenceIds={regulatoryReferenceIds}
        onRegulatoryReferenceChange={setRegulatoryReferenceIds}
        documentTypeIds={documentTypeIds}
        onDocumentTypeChange={setDocumentTypeIds}
        onSearch={() => applyFilters()}
        searching={searching}
      />

      {error && <div className="alert alert-error" style={{ marginTop: '15px' }}>{error}</div>}

      {activeQuery && (
        <div style={{ marginTop: '15px' }}>
          <SearchAnswer result={result} />

          <div className="card">
            <div className="card-header">
              <h3>{result?.is_answered ? 'Other matches' : 'Matching documents'}</h3>
            </div>
            {searching && <div className="loading">Searching…</div>}
            {!searching && documents.length === 0 && (
              <div className="empty-state">
                <p>No active document matches that.</p>
                {scope === 'mine' && (
                  <button className="btn btn-secondary"
                          onClick={() => applyFilters({ scope: 'all' })}>
                    🌐 Show all locations
                  </button>
                )}
              </div>
            )}
            {!searching && documents.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {documents.map(doc => (
                  <li key={doc.id}
                      style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    {/* state.from mirrors ApprovalsPage/InsightsPage's own links --
                        DocumentViewPage's back button uses it to return here via
                        browser history, restoring this exact search (query, mode,
                        filters) instead of sending the reader to the generic
                        Documents list they may not even have a link to. */}
                    <Link to={`/documents/${doc.id}`} state={{ from: '/' }}>
                      <strong>{doc.title}</strong>
                    </Link>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                      {doc.hierarchy_display_path || '—'}
                      {doc.version_label && ` · ${doc.version_label}`}
                      {doc.policy_areas?.length > 0
                        && ` · ${doc.policy_areas.map(pa => pa.name).join(', ')}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Below the results, and directly below the search bar before any search
          -- exactly what the issue asks for. */}
      <div style={{ marginTop: '15px' }}>
        <StartPageWidgets scope={scope} />
      </div>
    </div>
  );
}

export default StartPage;
